import "./ClienteFormModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { PromotorResponse } from "../../../shared/response/terceros/PromotorResponse";
import { ClientePayload } from "../../../shared/services/api/payload/terceros/ClientePayload";
import { ClienteSaveService } from "../../services/terceros/ClienteSaveService";
import { PromotorService } from "../../services/terceros/PromotorService";

export class ClienteFormModal extends BaseSmallModalFormComponent<
  ClienteResponse,
  ClientePayload,
  ClienteSaveService
> {
  private _razonSocialInput!: HTMLInputElement;
  private _nitInput!: HTMLInputElement;
  private _emailInput!: HTMLInputElement;
  private _telefonoInput!: HTMLInputElement;
  private _promotorSelect!: HTMLSelectElement;

  /**
   * @param _item Cliente a editar; sin él se crea uno nuevo.
   * @param _prefill Valores con los que arranca el formulario de uno nuevo. Lo usa el wizard de proyectos
   *   para traerse lo que ya se escribió en el buscador de beneficiarios y no hacerlo teclear dos veces.
   *   No convierte el modal en edición: sigue sin `id` y sigue siendo un alta.
   */
  constructor(
    private readonly _onSaved: (item: ClienteResponse) => void,
    private readonly _item?: ClienteResponse,
    private readonly _prefill?: { nombre?: string; nit?: string },
  ) {
    super(new ClienteSaveService());
  }

  protected get title(): string {
    return this._item ? "Editar cliente" : "Nuevo cliente";
  }

  protected renderBody(): string {
    const razonSocial = this._item?.nombre   ?? this._prefill?.nombre ?? "";
    const nit         = this._item?.nit       ?? this._prefill?.nit    ?? "";
    const email       = this._item?.email     ?? "";
    const telefono    = this._item?.telefono  ?? "";

    return `
      <div class="cl-form-fields">
        <div class="cl-form-group">
          <label class="cl-form-label" for="cl-f-razon">
            Razón social / Nombre <span class="cl-required">*</span>
          </label>
          <input
            id="cl-f-razon"
            class="sx-input"
            type="text"
            placeholder="Nombre o razón social del cliente"
            value="${this._esc(razonSocial)}"
            maxlength="250"
          />
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="cl-f-nit">
            NIT / Documento <span class="cl-required">*</span>
          </label>
          <input
            id="cl-f-nit"
            class="sx-input"
            type="text"
            placeholder="Ej: 900123456-1"
            value="${this._esc(nit)}"
            maxlength="100"
          />
          <span class="cl-form-hint">Debe ser único entre clientes activos.</span>
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="cl-f-email">Email</label>
          <input
            id="cl-f-email"
            class="sx-input"
            type="email"
            placeholder="correo@empresa.com"
            value="${this._esc(email)}"
            maxlength="250"
          />
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="cl-f-telefono">Teléfono</label>
          <input
            id="cl-f-telefono"
            class="sx-input"
            type="tel"
            placeholder="+57 300 000 0000"
            value="${this._esc(telefono)}"
            maxlength="30"
          />
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="cl-f-promotor">Promotor</label>
          <!-- placeholder síncrono; onAfterRender reemplaza las opciones con la lista real -->
          <select id="cl-f-promotor" class="sx-input">
            <option value="">Cargando promotores…</option>
          </select>
        </div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._razonSocialInput = el.querySelector<HTMLInputElement>("#cl-f-razon")!;
    this._nitInput         = el.querySelector<HTMLInputElement>("#cl-f-nit")!;
    this._emailInput       = el.querySelector<HTMLInputElement>("#cl-f-email")!;
    this._telefonoInput    = el.querySelector<HTMLInputElement>("#cl-f-telefono")!;
    this._promotorSelect   = el.querySelector<HTMLSelectElement>("#cl-f-promotor")!;

    this._razonSocialInput.addEventListener("input", () => this._validate());
    this._nitInput.addEventListener("input", () => this._validate());
    this._validate();

    this._loadPromotores();
  }

  protected buildPayload(): ClientePayload {
    return {
      id:          this._item?.id ?? null,
      razonSocial: this._razonSocialInput.value.trim(),
      nit:         this._nitInput.value.trim(),
      email:       this._emailInput.value.trim(),
      telefono:    this._telefonoInput.value.trim(),
      promotorId:  this._promotorSelect.value || null, // string vacío → null; la API espera null cuando no hay promotor
    };
  }

  protected override onSaved(item: ClienteResponse): void {
    this._onSaved(item);
    this.dispose();
  }

  private _validate(): void {
    const ok =
      this._razonSocialInput.value.trim().length >= 3 &&
      this._nitInput.value.trim().length >= 1;
    this.setSaveDisabled(!ok);
  }

  private async _loadPromotores(): Promise<void> {
    try {
      const promotores = await PromotorService.instance.get();
      this._populatePromotorSelect(promotores);
    } catch {
      this._promotorSelect.innerHTML = '<option value="">Sin promotores disponibles</option>';
    }
  }

  private _populatePromotorSelect(promotores: PromotorResponse[]): void {
    const currentId = this._item?.promotorId ?? null;
    const options = [
      `<option value="">Sin promotor</option>`,
      ...promotores.map(
        (p) =>
          `<option value="${p.id}" ${p.id === currentId ? "selected" : ""}>${this._esc(p.nombre)}</option>`,
      ),
    ];
    this._promotorSelect.innerHTML = options.join("");
  }

  // evita XSS al inyectar texto del usuario en atributos value="" del HTML del form
  private _esc(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
